import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Package, Briefcase, MapPin, Layers } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/motion";
import { DistributorForm } from "@/components/forms";
import { useSite } from "@/context/SiteContext";
import { waLink } from "@/lib/api";

const benefits = [
  { icon: Layers, title: "Product Portfolio", text: "Access to ICROPSIA maize and wheat seed varieties." },
  { icon: Package, title: "Professional Packaging", text: "Retail-ready branded packaging with full label information." },
  { icon: Briefcase, title: "Business Partnership", text: "Build a long-term relationship with a growing agricultural brand." },
  { icon: MapPin, title: "Regional Opportunity", text: "Serve farmers in Bihar through a focused agricultural product portfolio." },
];

export function FarmerSection() {
  return (
    <section data-testid="farmer-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1574943320219-553eb213f72d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Indian farmer inspecting crop in the field"
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-ink/70" />
        <div className="absolute inset-0 grain opacity-30" />
      </div>
      <div className="container-site relative max-w-3xl">
        <Reveal>
          <Eyebrow light>Farmer First</Eyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.1]">
            Supporting the People Behind <span className="font-editorial italic font-semibold text-brand-wheat">Every Harvest</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/75 leading-relaxed max-w-xl">
            Farming is built on experience, timing, trust, and the right inputs. ICROPSIA aims to support farmers and
            agricultural partners with quality-focused seed products and dependable access to information.
          </p>
          <Link
            to="/products"
            data-testid="farmer-explore-cta"
            className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-7 py-4 text-sm font-bold text-brand-deep transition-all hover:bg-brand-wheat"
          >
            Explore Our Products <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function DistributorSection() {
  const { settings } = useSite();
  return (
    <section data-testid="distributor-section" className="py-20 sm:py-28 bg-brand-beige/60">
      <div className="container-site">
        <div className="bg-brand-deep text-white rounded-[2.5rem] relative overflow-hidden shadow-2xl shadow-brand-deep/30">
          <div className="absolute inset-0 grain opacity-20" />
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-gold/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 p-7 sm:p-12 lg:p-16">
            <div>
              <Reveal>
                <Eyebrow light>Partnership</Eyebrow>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]">
                  Grow Your Agricultural Business With <span className="font-editorial italic font-semibold text-brand-wheat">ICROPSIA</span>
                </h2>
                <p className="mt-5 text-white/70 leading-relaxed">
                  We work with agricultural distributors, seed dealers, retailers, and local agri-businesses looking to
                  add quality-focused seed products to their portfolio.
                </p>
              </Reveal>
              <div className="mt-9 grid sm:grid-cols-2 gap-5">
                {benefits.map(({ icon: Icon, title, text }, i) => (
                  <Reveal key={title} delay={i * 0.08}>
                    <div className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 hover:border-brand-gold/40 transition-all">
                      <Icon className="w-5 h-5 text-brand-gold mb-3" />
                      <h3 className="font-display font-bold text-sm">{title}</h3>
                      <p className="mt-1 text-xs text-white/60 leading-relaxed">{text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.3}>
                <div className="mt-8 flex flex-wrap gap-4">
                  <a
                    href="#partnership-form"
                    data-testid="distributor-apply-cta"
                    className="inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-6 py-3.5 text-sm font-bold text-brand-deep transition-all hover:bg-brand-wheat"
                  >
                    Apply to Become a Distributor <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href={waLink(settings, "Hello ICROPSIA team, I am interested in becoming a distributor/dealer. Please share partnership details.")}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="distributor-whatsapp-cta"
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/30 px-6 py-3.5 text-sm font-bold text-white transition-all hover:bg-white hover:text-brand-deep"
                  >
                    <MessageCircle className="w-4 h-4" /> Talk to Our Team
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:pl-4">
              <div id="partnership-form" className="bg-brand-ivory text-brand-charcoal rounded-3xl p-6 sm:p-8 shadow-2xl scroll-mt-28">
                <h3 className="font-display text-xl sm:text-2xl font-extrabold text-brand-deep tracking-tight">
                  Interested in Stocking ICROPSIA Seeds?
                </h3>
                <p className="mt-2 text-sm text-brand-charcoal/60">
                  Fill in your details and our team will reach out with partnership information.
                </p>
                <div className="mt-6">
                  <DistributorForm />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
